import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { AlertCircle } from "lucide-react"
import { useEffect, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { useMediaQuery } from "@uidotdev/usehooks"
import useCoursesInfo from "@/modules/courses/hooks/use-courses-info"
import { PRODUCT_TOUR_STEPS } from "@/modules/product-tour/constants/steps"
import { Badge } from "@/components/ui/badge"
import { useSelectedCourse } from "@/modules/tracking/stores/use-selected-course"
import useGetMySituation from "@/modules/tracking/students-tracking/queries/use-get-situation"
import CoursesSelector from "@/modules/tracking/components/course-selector/course-selector"
import CoursesProgress from "@/modules/tracking/components/course-progress/course-progress"
import MetricCard from "@/modules/tracking/components/metric-card/metric-card"
import Modules from "@/modules/tracking/components/modules-list/modules"
import ObjectivesList from "@/modules/tracking/students-tracking/components/objectives-list"
import MilestoneList from "@/modules/tracking/students-tracking/components/milestones"
import DominatedUnitsCard from "@/modules/tracking/students-tracking/components/dominated-units-card"
import { VideoMiniModal } from "@/modules/product-tour-v2/components/video-mini-modal"
import useControlProductTour from "@/modules/product-tour-v2/hooks/use-controls"
import { PRODUCT_TOUR_V2_STEPS } from "@/modules/product-tour-v2/types/steps"
import { useProductTourV2 } from "@/modules/product-tour-v2/stores/use-product-tour"

export default function MySituationPage() {
  const navigate = useNavigate()
  const { coursesInfo, isError: isCoursesError } = useCoursesInfo()
  const { setSelectedCourse, selectedCourse } = useSelectedCourse()
  const { data, isSuccess, isError } = useGetMySituation()
  const isMobile = useMediaQuery("(max-width: 768px)")
  const isDesktop = useMediaQuery("(min-width: 768px) and (max-width: 1279px)")
  const isLargeDesktop = useMediaQuery("(min-width: 1280px)")
  const { showVideo } = useControlProductTour()
  const { currentStep } = useProductTourV2()

  const totalUnits = useMemo(() => {
    if (!data) return 0
    return data.modules.reduce((total, module) => total + module.units.length, 0)
  }, [!!data])

  useEffect(() => {
    if (coursesInfo.courses.length > 0) {
      setSelectedCourse(coursesInfo.courses[0])
    }
  }, [coursesInfo])

  if (isError || isCoursesError) {
    return (
      <section className="flex size-full flex-col items-center justify-center space-y-6 p-6">
        <AlertCircle className="size-16 text-destructive" />
        <p className="text-lg text-muted-foreground">
          Hubo un error al cargar tu situación. Por favor, intenta nuevamente más tarde.
        </p>
        <Button onClick={() => navigate(-1)}>Volver a la página anterior</Button>
      </section>
    )
  }

  return (
    <section
      className="size-full space-y-6 bg-neutral-50 p-6"
      id={PRODUCT_TOUR_STEPS.MY_SITUATION_START}
    >
      <section className="w-fit space-y-4">
        <article className="inline-flex items-center gap-2">
          <h1 className="text-xl font-bold text-foreground">Mi situación</h1>
          {data && (
            <Badge className="border-primary-500 bg-primary-50 py-1 text-primary-500">
              Semana {data.currentWeekNumber}
            </Badge>
          )}
        </article>
        {coursesInfo.courses.length > 0 ? (
          <CoursesSelector
            courses={coursesInfo.courses ?? []}
            selectedCourse={selectedCourse}
            setSelectedCourse={setSelectedCourse}
          />
        ) : (
          <Skeleton className="size-10 w-full rounded-lg bg-gray-200" />
        )}
      </section>
      {/** Large Desktop */}
      {isLargeDesktop && (
        <section className="flex gap-6">
          <article className="flex w-[70%] flex-1 flex-col gap-6">
            <section className="flex items-center gap-6">
              {isSuccess ? (
                <CoursesProgress
                  courseProgress={data.courseProgress}
                  courseStatus={data.courseStatus}
                />
              ) : (
                <Skeleton className="min-h-[162px] flex-1 rounded-lg bg-gray-200" />
              )}
              {isSuccess ? (
                <MetricCard
                  title="Avance de la semana"
                  type="week"
                  value={data.weeklyCourseProgress}
                  diff={data.weeklyCourseProgressDiff}
                />
              ) : (
                <Skeleton className="min-h-[162px] flex-1 rounded-lg bg-gray-200" />
              )}
            </section>
            <section className="flex h-fit w-full gap-6">
              {isSuccess ? (
                <Modules modules={data.modules} />
              ) : (
                <Skeleton className="h-56 w-full bg-gray-200" />
              )}
            </section>
          </article>
          <article className="flex w-[30%] flex-1 flex-col gap-6">
            {isSuccess ? (
              <section className="grid grid-cols-2 gap-6">
                <MetricCard
                  title="Actividades realizadas"
                  type="activities"
                  value={data.approvedActivitiesProgress}
                  diff={data.approvedActivitiesDiff}
                />
                <DominatedUnitsCard
                  value={data.masteredUnits}
                  diff={data.masteredUnitsDiff}
                  totalUnits={totalUnits}
                />
              </section>
            ) : (
              <div className="grid grid-cols-3 gap-6">
                <Skeleton className="aspect-square min-h-[162px] bg-gray-200" />
                <Skeleton className="aspect-square min-h-[162px] bg-gray-200" />
                <Skeleton className="aspect-square min-h-[162px] bg-gray-200" />
              </div>
            )}
            {isSuccess && (
              <section className="flex flex-col gap-4">
                <ObjectivesList objectives={data.approvedWeeklyObjectives} />
                <MilestoneList milestones={data.milestones} />
              </section>
            )}
          </article>
        </section>
      )}
      {/** Desktop */}
      {isDesktop && (
        <section className="flex flex-col gap-6">
          {isSuccess ? (
            <CoursesProgress
              courseProgress={data.courseProgress}
              courseStatus={data.courseStatus}
            />
          ) : (
            <Skeleton className="min-h-[162px] flex-1 rounded-lg bg-gray-200" />
          )}
          <section className="grid grid-cols-3 gap-6">
            {isSuccess ? (
              <>
                <MetricCard
                  title="Avance de la semana"
                  type="week"
                  value={data.weeklyCourseProgress}
                  diff={data.weeklyCourseProgressDiff}
                />
                <MetricCard
                  title="Actividades realizadas"
                  type="activities"
                  value={data.approvedActivitiesProgress}
                  diff={data.approvedActivitiesDiff}
                />
                <DominatedUnitsCard
                  value={data.masteredUnits}
                  diff={data.masteredUnitsDiff}
                  totalUnits={totalUnits}
                />
              </>
            ) : (
              <>
                <Skeleton className="aspect-square min-h-[162px] bg-gray-200" />
                <Skeleton className="aspect-square min-h-[162px] bg-gray-200" />
                <Skeleton className="aspect-square min-h-[162px] bg-gray-200" />
              </>
            )}
          </section>
          {isSuccess ? (
            <Modules modules={data.modules} />
          ) : (
            <Skeleton className="h-56 w-full bg-gray-200" />
          )}
          {isSuccess && (
            <section className="flex flex-col gap-4">
              <ObjectivesList objectives={data.approvedWeeklyObjectives} />
              <MilestoneList milestones={data.milestones} />
            </section>
          )}
        </section>
      )}

      {/* Mobile */}
      {isMobile && (
        <section className="flex flex-col gap-6">
          <article className="flex flex-1 flex-col gap-6">
            <section className="flex flex-col gap-6">
              {isSuccess ? (
                <CoursesProgress
                  courseProgress={data.courseProgress}
                  courseStatus={data.courseStatus}
                />
              ) : (
                <Skeleton className="min-h-[162px] flex-1 rounded-lg bg-gray-200" />
              )}
              {isSuccess ? (
                <>
                  <MetricCard
                    title="Avance de la semana"
                    type="week"
                    value={data.weeklyCourseProgress}
                    diff={data.weeklyCourseProgressDiff}
                  />
                  <MetricCard
                    title="Actividades realizadas"
                    type="activities"
                    value={data.approvedActivitiesProgress}
                    diff={data.approvedActivitiesDiff}
                  />
                  <DominatedUnitsCard
                    value={data.masteredUnits}
                    diff={data.masteredUnitsDiff}
                    totalUnits={totalUnits}
                  />
                </>
              ) : (
                <Skeleton className="min-h-[162px] flex-1 rounded-lg bg-gray-200" />
              )}
            </section>
            <section className="flex h-fit w-full gap-6">
              {isSuccess ? (
                <Modules modules={data.modules} />
              ) : (
                <Skeleton className="h-56 w-full bg-gray-200" />
              )}
            </section>
          </article>
          <article className="flex flex-col gap-6">
            {isSuccess && (
              <section className="flex flex-col gap-4">
                <ObjectivesList objectives={data.approvedWeeklyObjectives} />
                <MilestoneList milestones={data.milestones} />
              </section>
            )}
          </article>
        </section>
      )}
      {showVideo && currentStep === PRODUCT_TOUR_V2_STEPS.MY_SITUATION && <VideoMiniModal />}
    </section>
  )
}
